import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  RotateCcw,
  Search,
  User,
  X,
} from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useFacilityTypes } from '../../hooks/useFacilities';
import { handleApiError, recommendationsApi } from '../../lib/api-client';
import { cn } from '../../lib/utils';
import type { RecommendationListItemDto } from '../../types/api';
import SingleLocationSelector from '../SingleLocationSelector';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RecommendationsPanelProps {
  assessmentType: 'HYGIENE' | 'COMPLIANCE';
}

type StatusFilter = 'all' | 'pending' | 'resolved' | 'overdue';

interface Filters {
  search: string;
  status: StatusFilter;
  facilityTypeId: string;
  startDate: string;
  endDate: string;
  locationId: string;
}

const DEFAULT_FILTERS: Filters = {
  search: '',
  status: 'all',
  facilityTypeId: '',
  startDate: '',
  endDate: '',
  locationId: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isOverdue(item: RecommendationListItemDto): boolean {
  if (item.isResolved) return false;
  if (!item.deadline) return false;
  return new Date(item.deadline) < new Date();
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function filtersToParams(
  filters: Filters,
  assessmentType: 'HYGIENE' | 'COMPLIANCE',
  page: number
) {
  const params: Record<string, string | number> = { page, limit: 20, assessmentType };
  if (filters.search) params.search = filters.search;
  if (filters.facilityTypeId) params.facilityTypeId = filters.facilityTypeId;
  if (filters.startDate) params.startDate = filters.startDate;
  if (filters.endDate) params.endDate = filters.endDate;
  if (filters.locationId) params.locationId = filters.locationId;
  if (filters.status === 'resolved') params.isResolved = 'true';
  if (filters.status === 'pending') params.isResolved = 'false';
  if (filters.status === 'overdue') {
    params.isResolved = 'false';
    params.overdue = 'true';
  }
  return params as any;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ item }: { item: RecommendationListItemDto }) {
  if (item.isResolved) {
    return (
      <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700'>
        <CheckCircle2 className='h-3 w-3' />
        Resolved
      </span>
    );
  }
  if (isOverdue(item)) {
    return (
      <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-red-50 text-red-700'>
        <AlertCircle className='h-3 w-3' />
        Overdue
      </span>
    );
  }
  return (
    <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-50 text-amber-700'>
      <Clock className='h-3 w-3' />
      Pending
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr className='border-b border-border'>
      {[1, 2, 3, 4, 5, 6, 7].map(i => (
        <td key={i} className='py-3 px-3'>
          <div className='h-4 bg-muted rounded animate-pulse' style={{ width: `${50 + (i * 13) % 40}%` }} />
        </td>
      ))}
    </tr>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export default function RecommendationsPanel({ assessmentType }: RecommendationsPanelProps) {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [locationSelectorKey, setLocationSelectorKey] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const { data: facilityTypes } = useFacilityTypes();

  const hasActiveFilters =
    filters.search !== '' ||
    filters.status !== 'all' ||
    filters.facilityTypeId !== '' ||
    filters.startDate !== '' ||
    filters.endDate !== '' ||
    filters.locationId !== '';

  const queryParams = filtersToParams(filters, assessmentType, page);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recommendations-panel', assessmentType, filters, page],
    queryFn: () => recommendationsApi.getAll(queryParams),
    placeholderData: (prev: any) => prev,
  });

  const resolveMutation = useMutation({
    mutationFn: ({ id, resolved }: { id: string; resolved: boolean }) =>
      recommendationsApi.markResolved(id, resolved),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recommendations-panel', assessmentType] });
      toast.success('Recommendation updated');
    },
    onError: handleApiError,
  });

  function handleReset() {
    setFilters(DEFAULT_FILTERS);
    setLocationSelectorKey(k => k + 1);
    setPage(1);
  }

  function handleFilterChange<K extends keyof Filters>(key: K, value: Filters[K]) {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(1);
  }

  async function handleExport() {
    setIsExporting(true);
    try {
      const exportParams = { ...queryParams };
      delete (exportParams as any).page;
      delete (exportParams as any).limit;
      const blob = await recommendationsApi.export(exportParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recommendations-${assessmentType.toLowerCase()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }

  const pagination = data?.pagination;
  const items = data?.data ?? [];
  const totalItems = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  return (
    <div className='space-y-4'>
      {/* Header row */}
      <div className='flex items-center justify-between gap-4 flex-wrap'>
        <div className='flex items-center gap-2'>
          {totalItems > 0 && (
            <span className='inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-primary/10 text-primary'>
              {totalItems.toLocaleString()}
            </span>
          )}
        </div>
        <Button onClick={handleExport} loading={isExporting} variant='outline' size='sm' className='gap-2'>
          <Download className='h-4 w-4' />
          Export Excel
        </Button>
      </div>

      {/* Filters */}
      <div className='flex flex-wrap gap-3 items-end'>
        <div className='w-56'>
          <Input
            label='Search'
            placeholder='Search recommendations...'
            value={filters.search}
            onChange={e => handleFilterChange('search', e.target.value)}
            inputPrefix={<Search className='h-4 w-4' />}
          />
        </div>

        <div className='w-40'>
          <Input
            type='select'
            label='Status'
            value={filters.status}
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'pending', label: 'Pending' },
              { value: 'resolved', label: 'Resolved' },
              { value: 'overdue', label: 'Overdue' },
            ]}
            onChange={e => handleFilterChange('status', String(e.target.value) as StatusFilter)}
          />
        </div>

        <div className='w-48'>
          <Input
            type='select'
            label='Facility Type'
            value={filters.facilityTypeId}
            options={[
              { value: '', label: 'All facility types' },
              ...(facilityTypes ?? []).map(ft => ({ value: ft.id, label: ft.name })),
            ]}
            onChange={e => handleFilterChange('facilityTypeId', String(e.target.value))}
          />
        </div>

        <div className='w-40'>
          <Input
            type='date'
            label='Start Date'
            value={filters.startDate}
            onChange={e => handleFilterChange('startDate', e.target.value)}
          />
        </div>

        <div className='w-40'>
          <Input
            type='date'
            label='End Date'
            value={filters.endDate}
            onChange={e => handleFilterChange('endDate', e.target.value)}
          />
        </div>

        <div className='flex flex-col gap-1'>
          <span className='text-sm font-medium text-gray-700'>Location</span>
          <SingleLocationSelector
            key={locationSelectorKey}
            compact
            selectedLocationId={filters.locationId || undefined}
            onLocationChange={(id) => handleFilterChange('locationId', id ?? '')}
          />
        </div>

        {hasActiveFilters && (
          <Button variant='ghost' size='sm' onClick={handleReset} className='gap-1.5 mb-0.5'>
            <RotateCcw className='h-3.5 w-3.5' />
            Reset
          </Button>
        )}
      </div>

      {/* Error */}
      {isError && !isLoading && (
        <div className='flex flex-col items-center justify-center py-14 gap-3'>
          <AlertCircle className='h-8 w-8 text-destructive' />
          <p className='text-sm font-medium text-destructive'>Failed to load recommendations.</p>
          <p className='text-xs text-muted-foreground'>{String((error as any)?.message ?? '')}</p>
        </div>
      )}

      {/* Table */}
      {!isError && (
        <div className='rounded-xl border border-border overflow-hidden bg-white'>
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead className='bg-muted/50 border-b border-border'>
                <tr>
                  <th className='text-left py-3 px-3 font-medium text-muted-foreground whitespace-nowrap'>Facility</th>
                  <th className='text-left py-3 px-3 font-medium text-muted-foreground'>Recommendation</th>
                  <th className='text-left py-3 px-3 font-medium text-muted-foreground whitespace-nowrap'>Deadline</th>
                  <th className='text-left py-3 px-3 font-medium text-muted-foreground whitespace-nowrap'>Status</th>
                  <th className='text-left py-3 px-3 font-medium text-muted-foreground whitespace-nowrap'>Assessed On</th>
                  <th className='text-left py-3 px-3 font-medium text-muted-foreground whitespace-nowrap'>Inspector</th>
                  <th className='py-3 px-3' />
                </tr>
              </thead>
              <tbody className='divide-y divide-border'>
                {isLoading
                  ? Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} />)
                  : items.length === 0
                  ? (
                    <tr>
                      <td colSpan={7} className='py-20 text-center'>
                        <div className='flex flex-col items-center gap-3'>
                          <Building2 className='h-8 w-8 text-muted-foreground' />
                          <p className='text-sm font-medium text-foreground'>No recommendations found</p>
                          <p className='text-xs text-muted-foreground'>Try adjusting your filters.</p>
                        </div>
                      </td>
                    </tr>
                  )
                  : items.map(item => (
                    <tr key={item.id} className='hover:bg-muted/20 transition-colors'>
                      {/* Facility */}
                      <td className='py-3 px-3'>
                        <div className='flex items-center gap-1.5'>
                          <Building2 className='h-3.5 w-3.5 text-muted-foreground flex-shrink-0' />
                          <span className='font-medium text-foreground truncate max-w-[160px]'>
                            {item.facility?.title ?? '—'}
                          </span>
                        </div>
                        {item.facilityType && (
                          <p className='text-xs text-muted-foreground mt-0.5 pl-5'>{item.facilityType.name}</p>
                        )}
                      </td>

                      {/* Recommendation text */}
                      <td className='py-3 px-3 max-w-xs'>
                        <p className='text-sm text-foreground line-clamp-2'>{item.recommendation}</p>
                      </td>

                      {/* Deadline */}
                      <td className='py-3 px-3 whitespace-nowrap'>
                        {item.deadline ? (
                          <div className='flex items-center gap-1.5'>
                            <Calendar className='h-3.5 w-3.5 text-muted-foreground flex-shrink-0' />
                            <span
                              className={cn(
                                'text-sm',
                                isOverdue(item) ? 'text-red-600 font-medium' : 'text-foreground'
                              )}
                            >
                              {formatDate(item.deadline)}
                            </span>
                          </div>
                        ) : (
                          <span className='text-muted-foreground'>—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className='py-3 px-3'>
                        <StatusBadge item={item} />
                      </td>

                      {/* Assessed On */}
                      <td className='py-3 px-3 whitespace-nowrap text-muted-foreground text-sm'>
                        {formatDate(item.assessedAt)}
                      </td>

                      {/* Inspector */}
                      <td className='py-3 px-3'>
                        {item.inspector ? (
                          <div className='flex items-center gap-1.5'>
                            <User className='h-3.5 w-3.5 text-muted-foreground flex-shrink-0' />
                            <span className='text-sm text-foreground truncate max-w-[120px]'>
                              {item.inspector.name}
                            </span>
                          </div>
                        ) : (
                          <span className='text-muted-foreground'>—</span>
                        )}
                      </td>

                      {/* Action */}
                      <td className='py-3 px-3 whitespace-nowrap'>
                        <Button
                          variant='ghost'
                          size='sm'
                          disabled={resolveMutation.isPending}
                          onClick={() =>
                            resolveMutation.mutate({ id: item.id, resolved: !item.isResolved })
                          }
                          className={cn(
                            'text-xs gap-1',
                            item.isResolved
                              ? 'text-muted-foreground hover:text-foreground'
                              : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                          )}
                        >
                          {item.isResolved ? (
                            <>
                              <X className='h-3 w-3' />
                              Unresolve
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className='h-3 w-3' />
                              Resolve
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!isLoading && pagination && totalPages > 1 && (
            <div className='flex items-center justify-between px-4 py-3 border-t border-border bg-white'>
              <p className='text-sm text-muted-foreground'>
                Page {pagination.currentPage} of {totalPages} &mdash; {totalItems.toLocaleString()} total
              </p>
              <div className='flex items-center gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className='gap-1'
                >
                  <ChevronLeft className='h-4 w-4' />
                  Prev
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className='gap-1'
                >
                  Next
                  <ChevronRight className='h-4 w-4' />
                </Button>
              </div>
            </div>
          )}

          {!isLoading && pagination && totalPages <= 1 && items.length > 0 && (
            <div className='px-4 py-3 border-t border-border bg-white'>
              <p className='text-sm text-muted-foreground'>
                {totalItems.toLocaleString()} item{totalItems !== 1 ? 's' : ''} total
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
